/**
 * Opening a room, and changing one which is already open.
 *
 * One dialog does both, because they are the same form: a name and a list of
 * people. Creating one sends everything that was picked; editing one sends
 * only what moved - who was added and who was taken out - so two people
 * changing a room at the same time do not undo each other's work by sending
 * the whole list back.
 *
 * The people are picked exactly the way an announcement picks the handful it
 * is addressed to: one control which draws its own list and searches through
 * it, and a chip per name underneath which puts the name back into the list.
 * A nickname typed by hand is a room somebody was never put into, so nothing
 * here takes one.
 *
 * The accounts are fetched once per page rather than once per opening. The
 * list does not change while somebody is looking at it, and the control
 * searches through what it was handed without asking again.
 */

import { query, queryAll } from "../core/dom";
import { getJson, postJson } from "../core/http";
import { text } from "../core/i18n";
import { ROW_FACE, faceImage, faceUrl } from "../core/avatar";
import { enhanceSelect } from "./select";
import type { ChatCandidateListResponse, ChatSummaryResponse } from "../core/types";

const CANDIDATES_URL = "/api/chat/candidates";

/** Shortest name a room may be given, mirroring what the endpoint accepts. */
export const NAME_MINIMUM = 2;

/** What the dialog is doing and what it was opened on. */
interface Editing {
    readonly room: ChatSummaryResponse | null;
    readonly before: ReadonlySet<number>;
    readonly chosen: Map<number, string>;
    readonly done: () => void;
}

let editing: Editing | null = null;
let picker: HTMLSelectElement | null = null;
let quiet = false;

/** Return the dialog of the page, or `null` when the page carries none. */
function dialog(): HTMLDialogElement | null {
    return query<HTMLDialogElement>("[data-chat-dialog]");
}

/** Return whether the browser really implements `<dialog>`. */
function dialogsWork(): boolean {
    return typeof HTMLDialogElement === "function" && typeof HTMLDialogElement.prototype.showModal === "function";
}

/** Show `message` above the buttons, or clear what is shown. */
export function showError(root: ParentNode, message: string | null): void {
    const notice = query<HTMLElement>("[data-chat-error]", root);
    const said = query<HTMLElement>("[data-chat-error-text]", root);

    if (!notice || !said) {
        return;
    }

    notice.hidden = message === null;
    said.textContent = message ?? "";
}

/** How large the face on one of the chips is drawn. */
const CHIP_FACE = 18;

/** Build the chip standing for one account which was picked. */
export function renderChosen(id: number, username: string): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "recipient";
    chip.dataset["chatChosen"] = String(id);
    chip.append(faceImage(username, CHIP_FACE));

    const label = document.createElement("span");
    label.textContent = username;

    const drop = document.createElement("button");
    drop.type = "button";
    drop.className = "recipient__drop";
    drop.dataset["chatDrop"] = String(id);
    drop.setAttribute("aria-label", text("js.chat.drop", "Odobrať {username}").replace("{username}", username));
    drop.innerHTML =
        '<svg class="icon" width="12" height="12" aria-hidden="true">' +
        '<use href="/static/assets/icons/icons.svg#close"></use></svg>';

    chip.append(label, drop);

    return chip;
}

/** Put the option of `id` back among the choices, or take it away. */
function offer(id: number, available: boolean): void {
    for (const option of Array.from(picker?.options ?? [])) {
        if (option.value === String(id)) {
            option.hidden = !available;
            option.disabled = !available;
        }
    }
}

/** Tell the list drawn over the control that its options moved. */
function repaintPicker(): void {
    if (picker === null) {
        return;
    }

    picker.selectedIndex = 0;
    quiet = true;
    picker.dispatchEvent(new Event("change", { bubbles: true }));
    quiet = false;
}

/** Return whether the form says something other than what it was opened on. */
export function hasChanges(root: ParentNode): boolean {
    if (editing === null) {
        return false;
    }

    const name = query<HTMLInputElement>("[data-chat-name]", root)?.value.trim() ?? "";

    if (editing.room === null) {
        return name.length >= NAME_MINIMUM && editing.chosen.size > 0;
    }

    if (name !== editing.room.name) {
        return true;
    }

    return (
        editing.chosen.size !== editing.before.size ||
        [...editing.chosen.keys()].some((id) => !editing?.before.has(id))
    );
}

/** Grey the saving button out while there is nothing to save. */
function followChanges(root: ParentNode): void {
    const save = query<HTMLButtonElement>("[data-chat-save]", root);
    if (save) {
        save.disabled = !hasChanges(root);
    }
}

/** Redraw the chips of everybody who is going to be in the room. */
function paintChosen(root: ParentNode): void {
    const list = query<HTMLElement>("[data-chat-chosen]", root);
    if (!list || editing === null) {
        return;
    }

    list.innerHTML = "";

    for (const [id, username] of editing.chosen) {
        list.appendChild(renderChosen(id, username));
    }

    followChanges(root);
}

/**
 * Build the control the people are picked with, once per page.
 *
 * Written by the browser rather than by the server because the dialog exists
 * on every page of the portal and almost nobody opens it: an account of the
 * whole portal rendered into every single page is a page paying for a list
 * nobody asked to see.
 */
async function buildPicker(box: HTMLElement): Promise<void> {
    const host = query<HTMLElement>("[data-chat-picker]", box);
    if (!host || picker !== null) {
        return;
    }

    const payload = await getJson<ChatCandidateListResponse>(CANDIDATES_URL);

    const select = document.createElement("select");
    select.id = "chat-room-people";
    select.dataset["chatSource"] = "";
    select.dataset["selectSearch"] = "";

    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.disabled = true;
    prompt.selected = true;
    prompt.textContent = text("js.chat.pick", "Vyberte hráča…");
    select.append(prompt);

    for (const account of payload.items) {
        const option = document.createElement("option");
        option.value = String(account.id);
        option.textContent = account.username;
        option.dataset["face"] = faceUrl(account.username, ROW_FACE);
        select.append(option);
    }

    host.append(select);
    enhanceSelect(select);

    // The label waited for the control; now there is one to point at.
    const label = query<HTMLLabelElement>("[data-chat-picker-label]", box);
    if (label) {
        label.htmlFor = select.id;
    }
    picker = select;

    select.addEventListener("change", () => {
        if (quiet || editing === null) {
            return;
        }

        const id = Number.parseInt(select.value, 10);
        const option = select.selectedOptions[0];

        if (Number.isFinite(id) && option && !editing.chosen.has(id)) {
            editing.chosen.set(id, (option.textContent ?? "").trim());
            offer(id, false);
            paintChosen(box);
        }

        // Back to the prompt, so the same name is never the answer twice and
        // the closed control keeps saying what the field is for.
        repaintPicker();
    });
}

/** Offer everybody again, minus the people the dialog was opened holding. */
function resetPicker(): void {
    for (const option of Array.from(picker?.options ?? [])) {
        const id = Number.parseInt(option.value, 10);
        if (Number.isFinite(id)) {
            offer(id, !editing?.chosen.has(id));
        }
    }

    repaintPicker();
}

/** Close the dialog, whatever it was opened for. */
export function closeChatDialog(): void {
    const box = dialog();

    editing = null;

    if (box?.open) {
        box.close();
    }
}

/** Send the form, and tell the caller when the room actually changed. */
async function submit(box: HTMLDialogElement): Promise<void> {
    if (editing === null) {
        return;
    }

    const name = query<HTMLInputElement>("[data-chat-name]", box)?.value.trim() ?? "";
    const chosen = [...editing.chosen.keys()];

    if (name.length < NAME_MINIMUM) {
        showError(box, text("js.chat.name_needed", "Konverzácia potrebuje meno."));

        return;
    }

    if (editing.room === null) {
        if (chosen.length === 0) {
            showError(box, text("js.chat.people_needed", "Vyberte aspoň jedného člena."));

            return;
        }

        await postJson(`/api/chat`, { name, member_ids: chosen });
    } else {
        await postJson(`/api/chat/${editing.room.id}/edit`, {
            name,
            add_member_ids: chosen.filter((id) => !editing?.before.has(id)),
            remove_member_ids: [...editing.before].filter((id) => !editing?.chosen.has(id)),
        });
    }

    const done = editing.done;
    closeChatDialog();
    done();
}

/** Wire the one dialog of the page, once. */
function bind(box: HTMLDialogElement): void {
    if (box.dataset["chatBound"] === "1") {
        return;
    }

    box.dataset["chatBound"] = "1";

    box.addEventListener("click", (event: Event) => {
        // The press lands on the cross inside the button, which is a symbol
        // rather than an element of the page - `Element` is what both are.
        const target = event.target;
        if (!(target instanceof Element) || editing === null) {
            return;
        }

        if (target.closest("[data-chat-cancel]")) {
            event.preventDefault();
            closeChatDialog();

            return;
        }

        const dropped = target.closest<HTMLElement>("[data-chat-drop]");
        if (dropped) {
            const id = Number.parseInt(dropped.dataset["chatDrop"] ?? "", 10);
            editing.chosen.delete(id);
            offer(id, true);
            repaintPicker();
            paintChosen(box);
        }
    });

    box.addEventListener("input", (event: Event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches("[data-chat-name]")) {
            followChanges(box);
        }
    });

    box.addEventListener("submit", (event: Event) => {
        event.preventDefault();

        void submit(box).catch((error: unknown) => {
            showError(box, error instanceof Error ? error.message : text("js.chat.error", "Niečo sa pokazilo."));
        });
    });
}

/**
 * Open the dialog on `room`, or on nothing at all to open a new one.
 *
 * `done` is called once the server accepted the change, which is what tells
 * the panel and the dock to fetch the rooms again.
 */
export function openChatDialog(room: ChatSummaryResponse | null, done: () => void): void {
    const box = dialog();
    if (!box || !dialogsWork()) {
        return;
    }

    bind(box);

    editing = {
        room,
        before: new Set(room?.members.map((member) => member.id) ?? []),
        chosen: new Map((room?.members ?? []).map((member) => [member.id, member.username])),
        done,
    };

    const title = query<HTMLElement>("[data-chat-dialog-title]", box);
    if (title) {
        title.textContent =
            room === null
                ? text("js.chat.new_title", "Nová konverzácia")
                : text("js.chat.edit_title", "Nastavenia konverzácie");
    }

    const save = query<HTMLButtonElement>("[data-chat-save]", box);
    if (save) {
        save.textContent = room === null ? text("js.chat.create", "Vytvoriť") : text("js.chat.save", "Uložiť");
    }

    const name = query<HTMLInputElement>("[data-chat-name]", box);
    if (name) {
        name.value = room?.name ?? "";
    }

    showError(box, null);
    paintChosen(box);
    box.showModal();
    name?.focus();

    void buildPicker(box)
        .then(() => resetPicker())
        .catch(() => undefined);
}

/** Forget the control of the page, used by the tests between two documents. */
export function resetChatDialog(): void {
    editing = null;
    picker = null;

    for (const stale of queryAll<HTMLElement>("[data-chat-picker] .select")) {
        stale.remove();
    }
}
